/**
 * 供应商（渠道）的完整表单：协议、地址、Key、速率与并发、适配选项、熔断策略。
 *
 * 背景：从旧的 Channels 页拆出；Alice 的供应商页右侧详情负责日常（熔断、Key、Logo），这里保留新建与全量编辑，节点参数一个不丢。
 * 设计意图：编辑时 API Key 留空表示沿用原 Key，只有填了才轮换；新建渠道必须填。
 * 关键约束：渠道 ID 创建后不能改（加密用途里含 ID）；Key 输入框用 type="text"（仓库约定），提交后立即清空。
 */
import { useState } from 'react'
import { catalogApi, type CatalogChannel } from '@ap/api-client/admin-catalog'
import type { ChannelInput, ProviderType } from '@ap/api-client/types'
import { A } from '@ap/i18n/admin'
import { C, errorText } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import {
  Button,
  Field,
  Form,
  Input,
  Modal,
  Notice,
  Select,
  Switch,
  useToast,
} from '@ap/ui'

export function ChannelForm({ channel, onClose, onSaved }: { channel: CatalogChannel | 'new' | null; onClose: () => void; onSaved: () => void }) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const isNew = channel === 'new'
  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [rpm, setRpm] = useState('6000')
  const [conc, setConc] = useState('100')
  const [enabled, setEnabled] = useState(true)
  const [drop, setDrop] = useState('')
  const [dropCache, setDropCache] = useState(false)
  const [streamUsage, setStreamUsage] = useState(true)
  const [http2, setHttp2] = useState(true)
  const [protocol, setProtocol] = useState<ProviderType>('openai_compat')
  const [maxCompletion, setMaxCompletion] = useState(false)
  const [injectReasoning, setInjectReasoning] = useState(false)
  const [retryStatuses, setRetryStatuses] = useState('')
  const [autoDisable, setAutoDisable] = useState(true)
  const [threshold, setThreshold] = useState('3')
  const [cooldown, setCooldown] = useState('120')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [shown, setShown] = useState<typeof channel>(null)

  if (channel !== shown) {
    setShown(channel)
    setError('')
    setApiKey('')
    if (channel) {
      const c = channel === 'new' ? null : channel
      setId(c?.id ?? '')
      setName(c?.name ?? '')
      setBaseUrl(c?.base_url ?? 'https://')
      setRpm(String(c?.rpm ?? 6000))
      setConc(String(c?.max_concurrent ?? 100))
      setEnabled(c?.enabled ?? true)
      setDrop((c?.options?.drop_params ?? []).join(', '))
      setDropCache(c?.options?.drop_prompt_cache_key ?? false)
      setStreamUsage(c?.options?.stream_usage ?? true)
      setHttp2(c?.options?.http2 ?? true)
      setProtocol(c?.provider_type ?? 'openai_compat')
      setMaxCompletion(c?.options?.max_completion_tokens ?? false)
      setInjectReasoning(c?.options?.inject_reasoning_content ?? false)
      setRetryStatuses((c?.options?.retry_statuses ?? []).join(', '))
      setAutoDisable(c?.auto_disable ?? true)
      setThreshold(String(c?.failure_threshold ?? 3))
      setCooldown(String(c?.cooldown_seconds ?? 120))
    }
  }

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const body: ChannelInput = {
        name: name.trim(),
        provider_type: protocol,
        base_url: baseUrl.trim(),
        max_concurrent: Number(conc),
        rpm: Number(rpm),
        enabled,
        options: {
          drop_params: drop
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          drop_prompt_cache_key: dropCache,
          stream_usage: streamUsage,
          http2,
          max_completion_tokens: maxCompletion,
          inject_reasoning_content: injectReasoning,
          retry_statuses: retryStatuses
            .split(',')
            .map((x) => Number(x.trim()))
            .filter((n) => Number.isInteger(n) && n >= 100 && n <= 599),
        },
        auto_disable: autoDisable,
        failure_threshold: Number(threshold),
        cooldown_seconds: Number(cooldown),
        ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      }
      const r = await catalogApi.putChannel(id.trim(), body)
      setApiKey('')
      toast.ok(t(A.models.savedVersion, { v: r.config_version }))
      onSaved()
    } catch (e) {
      setError(errorText(e, locale))
    } finally {
      setBusy(false)
    }
  }

  const hint = channel && channel !== 'new' ? channel.api_key_hint : ''
  return (
    <Modal open={channel !== null} onClose={onClose} busy={busy} wide title={isNew ? t(A.channels.create) : id}>
      <Form onSubmit={submit}>
        {error && <Notice tone="danger">{error}</Notice>}
        <div className="ap-grid2">
          <Field label={t(A.channels.id)} hint={isNew ? t(A.channels.idHint) : undefined}>
            <Input mono required disabled={!isNew} pattern="[a-z0-9][a-z0-9_\-]{1,62}" value={id} onChange={(e) => setId(e.target.value)} />
          </Field>
          <Field label={t(A.channels.name)}>
            <Input required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>
        <Field label={t(A.channels.protocol)}>
          <Select
            value={protocol}
            onChange={(e) => setProtocol(e.target.value as ProviderType)}
            options={(['openai_compat', 'openai', 'anthropic', 'gemini', 'minimax_video_v2', 'ark_seedance_v3', 'minimax_speech_async_v2', 'qwen_audio_31', 'tianpuyue_music_v1', 'volc_seed_audio_v1'] as const).map((p) => ({ value: p, label: p }))}
          />
        </Field>
        <Field label={t(A.channels.baseUrl)}>
          <Input mono required maxLength={500} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        </Field>
        <Field label={t(A.channels.apiKey)} hint={isNew ? undefined : t(A.channels.apiKeyKeep, { hint })}>
          <Input
            mono
            type="text"
            autoComplete="off"
            spellCheck={false}
            required={isNew}
            minLength={8}
            maxLength={4096}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </Field>
        <div className="ap-grid2">
          <Field label={t(A.channels.rpm)}>
            <Input type="number" min={1} max={1_000_000} required value={rpm} onChange={(e) => setRpm(e.target.value)} />
          </Field>
          <Field label={t(A.channels.concurrent)}>
            <Input type="number" min={1} max={100_000} required value={conc} onChange={(e) => setConc(e.target.value)} />
          </Field>
        </div>
        <Field label={t(A.channels.dropParams)} hint={t(A.channels.dropParamsHint)}>
          <Input mono value={drop} onChange={(e) => setDrop(e.target.value)} />
        </Field>
        <Field label={t(A.channels.retryStatuses)} hint={t(A.channels.retryStatusesHint)}>
          <Input mono value={retryStatuses} onChange={(e) => setRetryStatuses(e.target.value)} />
        </Field>
        <div className="ap-grid2">
          <Field label={t(A.channels.threshold)}>
            <Input type="number" min={1} max={100} required value={threshold} onChange={(e) => setThreshold(e.target.value)} />
          </Field>
          <Field label={t(A.channels.cooldown)}>
            <Input type="number" min={0} max={86400} required value={cooldown} onChange={(e) => setCooldown(e.target.value)} />
          </Field>
        </div>
        <div className="ap-stack" style={{ gap: 8 }}>
          <Switch checked={autoDisable} onChange={setAutoDisable} label={t(A.channels.autoDisable)} />
          <Switch checked={maxCompletion} onChange={setMaxCompletion} label={t(A.channels.maxCompletionTokens)} />
          <Switch checked={injectReasoning} onChange={setInjectReasoning} label={t(A.channels.injectReasoning)} />
          <Switch checked={enabled} onChange={setEnabled} label={t(A.models.enabled)} />
          <Switch checked={streamUsage} onChange={setStreamUsage} label={t(A.channels.streamUsage)} />
          <Switch checked={dropCache} onChange={setDropCache} label={t(A.channels.dropCacheKey)} />
          <Switch checked={http2} onChange={setHttp2} label={t(A.channels.http2)} />
        </div>
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t(C.save)}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

